import React, { useState, useEffect } from "react";
import { Link, usePage, router } from "@inertiajs/react";
import { Head } from "@inertiajs/react";
import {
  Bell,
  CircleUser,
  Home,
  Info,
  LineChart,
  Menu,
  Package,
  Package2,
  Users,
  Settings,
  Image,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import clsx from "clsx";

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
  storeName?: string;
  storeLogo?: string;
}

const navLinks = [
  { href: "/admin/dashboard", label: "Dashboard", icon: Home },
  { href: "/admin/products", label: "Productos", icon: Package },
  { href: "/admin/categories", label: "Categorías", icon: Users },
  { href: "/admin/brands", label: "Marcas", icon: Package2 },
  { href: "/admin/promotions", label: "Promociones", icon: LineChart },
  { href: "/admin/store_settings", label: "Configuración", icon: Settings },
  { href: "/admin/about", label: "Acerca de", icon: Info },
  { href: "/admin/hero_slides", label: "Slides Hero", icon: Image },
  { href: "/", label: "Ver Web", icon: Eye },
];

export default function DashboardLayout({
  children,
  title = "Panel de Control",
  storeName = "",
  storeLogo,
}: DashboardLayoutProps) {
  const { url, props } = usePage();
  const { flash } = props as { flash?: { notice?: string; alert?: string } };
  const [toast, setToast] = useState<{
    message: string;
    type: "notice" | "alert" | "";
  }>({ message: "", type: "" });

  useEffect(() => {
    if (flash?.notice) {
      setToast({ message: flash.notice, type: "notice" });
    } else if (flash?.alert) {
      setToast({ message: flash.alert, type: "alert" });
    }

    if (flash?.notice || flash?.alert) {
      const timer = setTimeout(() => {
        setToast({ message: "", type: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [flash]);

  const handleLogout = () => {
    router.delete("/admins/sign_out");
  };

  const renderNavLinks = (isMobile = false) => {
    return navLinks.map((link) => {
      const isActive = url.startsWith(link.href) && link.href !== "/";
      const isHomePage = link.href === "/" && url === "/";

      return (
        <Link
          key={link.href}
          href={link.href}
          className={clsx(
            "flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200 font-medium",
            {
              "bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-md":
                isActive || isHomePage,
              "text-slate-600 dark:text-slate-400 hover:bg-gradient-to-r hover:from-slate-100 hover:to-slate-200 dark:hover:from-slate-700 dark:hover:to-slate-600 hover:text-slate-800 dark:hover:text-slate-200":
                !isActive && !isHomePage,
              "mx-[-0.65rem] gap-4 px-3": isMobile,
            },
          )}
        >
          <link.icon className={clsx("h-5 w-5", { "h-6 w-6": isMobile })} />
          {link.label}
        </Link>
      );
    });
  };

  return (
    <div className="grid min-h-screen w-full md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr]">
      <Head title={title} />

      {/* Sidebar */}
      <div className="hidden border-r border-slate-200 dark:border-slate-700 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 md:block shadow-lg">
        <div className="flex h-full max-h-screen flex-col gap-2">
          <div className="flex h-14 items-center border-b border-slate-200 dark:border-slate-700 px-4 lg:h-[60px] lg:px-6 bg-gradient-to-r from-white to-slate-50 dark:from-slate-800 dark:to-slate-900">
            <div className="flex items-center gap-3 font-bold text-slate-800 dark:text-slate-200">
              {storeLogo ? (
                <img
                  src={storeLogo}
                  alt="Logo de la tienda"
                  className="h-6 w-6 object-contain"
                />
              ) : (
                <Package2 className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              )}
              <span className="text-lg">{storeName}</span>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="ml-auto h-9 w-9 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm"
            >
              <Bell className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              <span className="sr-only">Alternar notificaciones</span>
            </Button>
          </div>
          <div className="flex-1 px-3 py-4">
            <nav className="grid items-start gap-2 text-sm font-medium">
              {renderNavLinks()}
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col">
        <header className="flex h-14 items-center gap-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 px-4 lg:h-[60px] lg:px-6 shadow-sm">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0 md:hidden border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <Menu className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                <span className="sr-only">Alternar menú de navegación</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="flex flex-col bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800"
            >
              <nav className="grid gap-3 text-lg font-medium">
                <div className="flex items-center gap-3 text-lg font-bold mb-6 text-slate-800 dark:text-slate-200">
                  {storeLogo ? (
                    <img
                      src={storeLogo}
                      alt="Logo de la tienda"
                      className="h-5 w-5 object-contain"
                    />
                  ) : (
                    <Package2 className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                  )}
                  <span>test</span>
                </div>
                {renderNavLinks(true)}
              </nav>
            </SheetContent>
          </Sheet>
          <div className="w-full flex-1">
            <div className="relative max-w-md">
              <LineChart className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                type="search"
                placeholder="Buscar productos..."
                className="w-full appearance-none bg-white dark:bg-slate-800 pl-11 h-11 border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg shadow-sm"
              />
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full h-10 w-10 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm"
              >
                <CircleUser className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                <span className="sr-only">Alternar menú de usuario</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg"
            >
              <DropdownMenuLabel className="text-slate-800 dark:text-slate-200 font-semibold">
                Mi Cuenta
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
              <DropdownMenuItem className="hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
                Configuración
              </DropdownMenuItem>
              <DropdownMenuItem className="hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
                Soporte
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
              >
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="relative flex flex-1 flex-col gap-6 p-6 lg:gap-8 lg:p-8 bg-gradient-to-br from-slate-50/50 to-white dark:from-slate-900/50 dark:to-slate-800 min-h-0">
          {toast.message && (
            <div
              className={clsx(
                "fixed top-20 right-6 z-50 p-4 rounded-xl text-white shadow-xl border",
                {
                  "bg-gradient-to-r from-emerald-500 to-green-600 border-emerald-400":
                    toast.type === "notice",
                  "bg-gradient-to-r from-red-500 to-rose-600 border-red-400":
                    toast.type === "alert",
                },
              )}
            >
              {toast.message}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
